// Task #3875 — DTO risposta condivisi per il CRM master (frontend-master).
// ============================================================================
// SoT unica BE↔FE per gli endpoint montati su /api/master/crm
// (packages/backend/src/routes/crm/crm-master.routes.ts +
//  packages/backend/src/services/crm/crm-master.service.ts), consumati da
// packages/frontend-master/src/pages/crm-master/*.tsx.
//
// Shape verificate contro: SQL reale del service, colonne DB dev
// (information_schema, schema `master`) e risposte curl reali in dev.
//
// Convenzioni wire (stile packages/shared/src/pos/purchase-orders.ts):
//   * pg numeric / SUM(numeric)      → wireNumeric (union string|number: il
//     parser globale NUMERIC→number è attivo sul BE, ma i valori dentro
//     jsonb e i path pre-serializzati restano stringhe);
//   * COUNT(*)::int / colonne int    → z.number();
//   * timestamp/timestamptz/date     → wireTimestamp (ISO string sul wire,
//     Date lato BE pre-serializzazione);
//   * colonne NULLABLE o alias LEFT JOIN → .nullable() (+ .optional() se
//     presenti solo in alcune proiezioni della stessa risorsa);
//   * jsonb generici                 → z.unknown();
//   * SELECT alias.* → colonne note enumerate + .passthrough().
// ============================================================================

import { z } from 'zod';

/** pg numeric: number con il parser globale, stringa dentro jsonb/legacy. */
const wireNumeric = z.union([z.string(), z.number()]);

/** timestamptz/date: ISO string sul wire, Date lato BE pre-serializzazione. */
const wireTimestamp = z.union([z.string(), z.date()]);

// ============================================================================
// CLIENTI (master.crm_customers)
// ============================================================================

/** Riga cliente master — SELECT c.* FROM master.crm_customers. */
export const crmMasterClientSchema = z
  .object({
    id: z.string(),
    customer_type: z.string(), // text NOT NULL
    company_name: z.string().nullable(),
    contact_name: z.string().nullable(),
    contact_first_name: z.string().nullable(),
    contact_last_name: z.string().nullable(),
    contact_email: z.string().nullable(),
    contact_phone: z.string().nullable(),
    vat_number: z.string().nullable(),
    fiscal_code: z.string().nullable(),
    sdi_code: z.string().nullable(),
    pec: z.string().nullable(),
    legal_address: z.string().nullable(),
    legal_city: z.string().nullable(),
    legal_province: z.string().nullable(),
    legal_zip: z.string().nullable(),
    legal_country: z.string().nullable(),
    legal_form: z.string().nullable(),
    iban: z.string().nullable(),
    bank_name: z.string().nullable(),
    sector: z.string().nullable(),
    client_notes: z.string().nullable(),
    fic_client_id: z.number().nullable(), // integer NULL
    stripe_customer_id: z.string().nullable(),
    created_at: wireTimestamp,
    updated_at: wireTimestamp,
  })
  .passthrough();
export type CrmMasterClient = z.infer<typeof crmMasterClientSchema>;

/** Riga lista clienti: cliente + conteggi aggregati (COUNT ::int). */
export const crmMasterClientListRowSchema = crmMasterClientSchema
  .extend({
    leads_count: z.number(),
    contacts_count: z.number(),
    quotes_count: z.number(),
    contracts_count: z.number(),
  })
  .passthrough();
export type CrmMasterClientListRow = z.infer<typeof crmMasterClientListRowSchema>;

/** GET /clients → data = { clients, total, page, limit } (wrapper annidato). */
export const crmMasterClientsListSchema = z
  .object({
    clients: z.array(crmMasterClientListRowSchema),
    total: z.number(),
    page: z.number(),
    limit: z.number(),
  })
  .passthrough();
export type CrmMasterClientsList = z.infer<typeof crmMasterClientsListSchema>;

/** Riga contatto cliente — SELECT * FROM master.crm_customer_contacts. */
export const crmMasterClientContactSchema = z
  .object({
    id: z.string(),
    customer_id: z.string(),
    first_name: z.string().nullable(),
    last_name: z.string().nullable(),
    role: z.string().nullable(),
    sector: z.string().nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    mobile: z.string().nullable(),
    notes: z.string().nullable(),
    is_primary: z.boolean(),
    created_at: wireTimestamp,
    updated_at: wireTimestamp,
  })
  .passthrough();
export type CrmMasterClientContact = z.infer<typeof crmMasterClientContactSchema>;

/** Lead sintetico dentro full-detail (proiezione esplicita + join stage). */
export const crmMasterClientLeadSummarySchema = z
  .object({
    id: z.string(),
    company_name: z.string().nullable(),
    contact_name: z.string().nullable(),
    opportunity_value: wireNumeric.nullable(),
    lead_source: z.string().nullable(),
    priority: z.string().nullable(),
    conversion_data_saved: z.boolean().nullable(),
    created_at: wireTimestamp.nullable(),
    updated_at: wireTimestamp.nullable(),
    stage_name: z.string().nullable(),
    stage_slug: z.string().nullable(),
    stage_color: z.string().nullable(),
  })
  .passthrough();
export type CrmMasterClientLeadSummary = z.infer<typeof crmMasterClientLeadSummarySchema>;

/** Item preventivo dentro full-detail: json_agg → numeri jsonb = stringhe. */
export const crmMasterClientQuoteItemSummarySchema = z
  .object({
    product_name: z.string().nullable(),
    quantity: wireNumeric.nullable(),
    unit_price: wireNumeric.nullable(),
    total: wireNumeric.nullable(),
    description: z.string().nullable(),
  })
  .passthrough();
export type CrmMasterClientQuoteItemSummary = z.infer<typeof crmMasterClientQuoteItemSummarySchema>;

/** Preventivo sintetico dentro full-detail. */
export const crmMasterClientQuoteSummarySchema = z
  .object({
    id: z.string(),
    quote_number: z.string().nullable(),
    status: z.string().nullable(),
    total_gross: wireNumeric.nullable(),
    total_vat: wireNumeric.nullable(),
    subtotal_net: wireNumeric.nullable(),
    created_at: wireTimestamp.nullable(),
    issue_date: wireTimestamp.nullable(),
    validity_date: wireTimestamp.nullable(),
    items: z.array(crmMasterClientQuoteItemSummarySchema).nullable(),
  })
  .passthrough();
export type CrmMasterClientQuoteSummary = z.infer<typeof crmMasterClientQuoteSummarySchema>;

/** Contratto sintetico dentro full-detail. */
export const crmMasterClientContractSummarySchema = z
  .object({
    id: z.string(),
    contract_number: z.string().nullable(),
    status: z.string().nullable(),
    signed_at: wireTimestamp.nullable(),
    start_date: wireTimestamp.nullable(),
    end_date: wireTimestamp.nullable(),
    duration_months: z.number().nullable(),
    contract_pipeline_status: z.string().nullable(),
    payment_suspended: z.boolean().nullable(),
    payment_suspended_at: wireTimestamp.nullable(),
    payment_suspended_by: z.string().nullable(),
    payment_suspension_reason: z.string().nullable(),
    installment_plan: z.unknown(), // jsonb
    quote_number: z.string().nullable(),
    quote_total: wireNumeric.nullable(),
  })
  .passthrough();
export type CrmMasterClientContractSummary = z.infer<typeof crmMasterClientContractSummarySchema>;

/** Fattura sintetica dentro full-detail. */
export const crmMasterClientInvoiceSummarySchema = z
  .object({
    id: z.string(),
    invoice_number: z.string().nullable(),
    status: z.string().nullable(),
    type: z.string().nullable(),
    total_gross: wireNumeric.nullable(),
    total_vat: wireNumeric.nullable(),
    subtotal_net: wireNumeric.nullable(),
    issue_date: wireTimestamp.nullable(),
    due_date: wireTimestamp.nullable(),
    amount_paid: wireNumeric.nullable(),
    fic_document_id: z.number().nullable(), // integer NULL
    fic_sdi_status: z.string().nullable(),
    e_invoice: z.boolean().nullable(),
    contract_number: z.string().nullable(),
  })
  .passthrough();
export type CrmMasterClientInvoiceSummary = z.infer<typeof crmMasterClientInvoiceSummarySchema>;

/** Rata sintetica dentro full-detail. */
export const crmMasterClientInstallmentSummarySchema = z
  .object({
    id: z.string(),
    installment_number: z.number().nullable(),
    amount: wireNumeric.nullable(),
    due_date: wireTimestamp.nullable(),
    status: z.string().nullable(),
    paid_at: wireTimestamp.nullable(),
    stripe_invoice_id: z.string().nullable(),
    is_entry_fee: z.boolean().nullable(),
    notes: z.string().nullable(),
    fic_document_id: z.number().nullable(),
    invoice_number: z.string().nullable(),
    contract_number: z.string().nullable(),
  })
  .passthrough();
export type CrmMasterClientInstallmentSummary = z.infer<typeof crmMasterClientInstallmentSummarySchema>;

/** Metodo di pagamento salvato (payment methods dentro full-detail). */
export const crmMasterClientPaymentMethodSchema = z
  .object({
    id: z.string(),
    stripe_pm_id: z.string().nullable(),
    stripe_pm_type: z.string().nullable(),
    stripe_pm_last4: z.string().nullable(),
    stripe_pm_brand: z.string().nullable(),
    stripe_pm_exp_month: z.number().nullable(),
    stripe_pm_exp_year: z.number().nullable(),
    is_default: z.boolean().nullable(),
    created_at: wireTimestamp.nullable(),
    quote_id: z.string().nullable(),
  })
  .passthrough();
export type CrmMasterClientPaymentMethod = z.infer<typeof crmMasterClientPaymentMethodSchema>;

/** Riquadro riepilogo dentro full-detail (conteggi ::int + SUM numeric). */
export const crmMasterClientSummarySchema = z
  .object({
    total_leads: z.number(),
    total_quotes: z.number(),
    active_contracts: z.number(),
    total_invoices: z.number(),
    total_installments: z.number(),
    installments_paid: z.number(),
    installments_pending: z.number(),
    installments_overdue: z.number(),
    amount_paid: wireNumeric,
    amount_pending: wireNumeric,
    amount_overdue: wireNumeric,
  })
  .passthrough();
export type CrmMasterClientSummary = z.infer<typeof crmMasterClientSummarySchema>;

/** GET /clients/:id/full-detail → aggregato cliente completo. */
export const crmMasterClientFullDetailSchema = z
  .object({
    client: crmMasterClientSchema.nullable(),
    contacts: z.array(crmMasterClientContactSchema),
    leads: z.array(crmMasterClientLeadSummarySchema),
    quotes: z.array(crmMasterClientQuoteSummarySchema),
    contracts: z.array(crmMasterClientContractSummarySchema),
    invoices: z.array(crmMasterClientInvoiceSummarySchema),
    installments: z.array(crmMasterClientInstallmentSummarySchema),
    payment_methods: z.array(crmMasterClientPaymentMethodSchema),
    summary: crmMasterClientSummarySchema,
  })
  .passthrough();
export type CrmMasterClientFullDetail = z.infer<typeof crmMasterClientFullDetailSchema>;

// ============================================================================
// AZIENDA (master.company_info) — GET /company → row | null
// ============================================================================
export const crmMasterCompanySchema = z
  .object({
    id: z.string(),
    company_name: z.string(),
    vat_number: z.string().nullable(),
    fiscal_code: z.string().nullable(),
    legal_address: z.string().nullable(),
    legal_city: z.string().nullable(),
    legal_province: z.string().nullable(),
    legal_zip: z.string().nullable(),
    legal_country: z.string().nullable(),
    iban: z.string().nullable(),
    bank_name: z.string().nullable(),
    sdi_code: z.string().nullable(),
    pec: z.string().nullable(),
    phone: z.string().nullable(),
    email: z.string().nullable(),
    website: z.string().nullable(),
    logo_url: z.string().nullable(),
    tax_regime: z.string().nullable(),
    ateco_code: z.string().nullable(),
    legal_representative: z.string().nullable(),
    share_capital: z.string().nullable(),
    rea_number: z.string().nullable(),
    created_at: wireTimestamp.nullable(),
    updated_at: wireTimestamp.nullable(),
  })
  .passthrough();
export type CrmMasterCompany = z.infer<typeof crmMasterCompanySchema>;

// ============================================================================
// PRODOTTI E LISTINI
// ============================================================================

/** GET /products → SELECT * FROM master.products (array nudo). */
export const crmMasterProductSchema = z
  .object({
    id: z.string(),
    code: z.string().nullable(),
    name: z.string(),
    description: z.string().nullable(),
    net_price: wireNumeric, // numeric NOT NULL
    vat_rate: wireNumeric.nullable(),
    recurrence_type: z.string(),
    unit_of_measure: z.string(),
    category: z.string().nullable(),
    fic_product_id: z.number().nullable(),
    stripe_price_id: z.string().nullable(),
    created_at: wireTimestamp.nullable(),
    updated_at: wireTimestamp.nullable(),
    vat_code: z.string().nullable(),
    nature: z.string(),
    status: z.string(),
    valid_from: wireTimestamp.nullable(),
    valid_to: wireTimestamp.nullable(),
  })
  .passthrough();
export type CrmMasterProduct = z.infer<typeof crmMasterProductSchema>;

/** GET /product-categories → SELECT * FROM master.product_categories. */
export const crmMasterProductCategorySchema = z
  .object({
    id: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    color: z.string().nullable(),
    sort_order: z.number().nullable(),
    is_active: z.boolean().nullable(),
    created_at: wireTimestamp.nullable(),
    updated_at: wireTimestamp.nullable(),
  })
  .passthrough();
export type CrmMasterProductCategory = z.infer<typeof crmMasterProductCategorySchema>;

/**
 * GET /price-lists → pl.* + items_count (::int, solo in lista);
 * GET /price-lists/:id → pl.* senza items_count (→ optional).
 */
export const crmMasterPriceListSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    is_default: z.boolean(),
    valid_from: wireTimestamp.nullable(),
    valid_to: wireTimestamp.nullable(),
    is_active: z.boolean(),
    created_at: wireTimestamp,
    updated_at: wireTimestamp,
    items_count: z.number().optional(),
  })
  .passthrough();
export type CrmMasterPriceList = z.infer<typeof crmMasterPriceListSchema>;

/**
 * GET /price-lists/:id/items → pli.* + alias prodotto.
 * NB: pli.vat_rate è varchar (stringa), product_vat_code/product_vat_rate
 * sono selezionati sempre NULL dalla query corrente.
 */
export const crmMasterPriceListItemSchema = z
  .object({
    id: z.string(),
    price_list_id: z.string(),
    product_id: z.string(),
    price: wireNumeric,
    discount_percent: wireNumeric,
    created_at: wireTimestamp,
    updated_at: wireTimestamp,
    vat_rate: z.string().nullable(), // varchar NULL (non numeric!)
    product_name: z.string().nullable(),
    product_code: z.string().nullable(),
    base_price: wireNumeric.nullable(),
    recurrence_type: z.string().nullable(),
    category: z.string().nullable(),
    product_vat_code: z.string().nullable(),
    product_vat_rate: wireNumeric.nullable(),
  })
  .passthrough();
export type CrmMasterPriceListItem = z.infer<typeof crmMasterPriceListItemSchema>;

/** GET /clients/:id/price-lists → tpl.* + alias listino (array nudo). */
export const crmMasterTenantPriceListSchema = z
  .object({
    id: z.string(),
    tenant_id: z.string(),
    price_list_id: z.string(),
    created_at: wireTimestamp,
    pipeline_entry_id: z.string().nullable(),
    price_list_name: z.string().nullable(),
    is_default: z.boolean().nullable(),
    is_active: z.boolean().nullable(),
    valid_from: wireTimestamp.nullable(),
    valid_to: wireTimestamp.nullable(),
  })
  .passthrough();
export type CrmMasterTenantPriceList = z.infer<typeof crmMasterTenantPriceListSchema>;

/** GET /resolve-price/:productId?tenant_id= → prezzo risolto per cliente. */
export const crmMasterResolvedPriceSchema = z
  .object({
    price: wireNumeric.nullable(),
    discount_percent: wireNumeric,
    price_list_name: z.string().nullable(),
    source: z.string(), // 'pipeline_entry' | 'tenant' | 'default' | 'base' | 'none'
  })
  .passthrough();
export type CrmMasterResolvedPrice = z.infer<typeof crmMasterResolvedPriceSchema>;

// ============================================================================
// PIPELINE
// ============================================================================

/** GET /pipeline/stages → SELECT * FROM master.pipeline_stages. */
export const crmMasterPipelineStageSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    slug: z.string().nullable(),
    color: z.string().nullable(),
    position: z.number().nullable(),
    is_final: z.boolean().nullable(),
    created_at: wireTimestamp.nullable(),
    updated_at: wireTimestamp.nullable(),
  })
  .passthrough();
export type CrmMasterPipelineStage = z.infer<typeof crmMasterPipelineStageSchema>;

/**
 * Riga pipeline entry — pipeline_entries.* + join tenant/stage.
 * I conteggi (::int) e i join sono presenti solo in alcune proiezioni
 * (lista/detail/conversion-data) → optional.
 */
export const crmMasterPipelineEntrySchema = z
  .object({
    id: z.string(),
    stage_id: z.string().nullable(),
    entered_at: wireTimestamp.nullable(),
    opportunity_value: wireNumeric.nullable(),
    notes: z.string().nullable(),
    loss_reason: z.string().nullable(),
    assigned_to: z.string().nullable(),
    created_at: wireTimestamp,
    updated_at: wireTimestamp,
    contact_name: z.string().nullable(),
    contact_email: z.string().nullable(),
    contact_phone: z.string().nullable(),
    lead_source: z.string().nullable(),
    priority: z.string().nullable(),
    next_followup_at: wireTimestamp.nullable(),
    tags: z.array(z.string()).nullable(),
    company_name: z.string().nullable(),
    vat_number: z.string().nullable(),
    fiscal_code: z.string().nullable(),
    sdi_code: z.string().nullable(),
    pec: z.string().nullable(),
    legal_address: z.string().nullable(),
    legal_city: z.string().nullable(),
    legal_province: z.string().nullable(),
    legal_zip: z.string().nullable(),
    legal_country: z.string().nullable(),
    legal_form: z.string().nullable(),
    conversion_data_saved: z.boolean().nullable(),
    contact_first_name: z.string().nullable(),
    contact_last_name: z.string().nullable(),
    stripe_customer_id: z.string().nullable(),
    stripe_payment_method_id: z.string().nullable(),
    stripe_pm_type: z.string().nullable(),
    stripe_pm_last4: z.string().nullable(),
    stripe_pm_brand: z.string().nullable(),
    payment_setup_token: z.string().nullable(),
    payment_setup_token_expires_at: wireTimestamp.nullable(),
    payment_setup_quote_id: z.string().nullable(),
    payment_setup_installment_plan: z.unknown(), // jsonb
    iban: z.string().nullable(),
    bank_name: z.string().nullable(),
    sector: z.string().nullable(),
    client_notes: z.string().nullable(),
    fic_client_id: z.number().nullable(),
    customer_id: z.string().nullable(),
    // Join tenant/stage (presenti in lista/detail).
    tenant_name: z.string().nullable().optional(),
    tenant_email: z.string().nullable().optional(),
    tenant_slug: z.string().nullable().optional(),
    stage_name: z.string().nullable().optional(),
    stage_slug: z.string().nullable().optional(),
    stage_color: z.string().nullable().optional(),
    stage_position: z.number().nullable().optional(),
    // Conteggi aggregati (solo lista).
    notes_count: z.number().optional(),
    attachments_count: z.number().optional(),
    pending_activities_count: z.number().optional(),
    quotes_count: z.number().optional(),
    contracts_count: z.number().optional(),
  })
  .passthrough();
export type CrmMasterPipelineEntry = z.infer<typeof crmMasterPipelineEntrySchema>;

/** Nota di una pipeline entry. */
export const crmMasterEntryNoteSchema = z
  .object({
    id: z.string(),
    entry_id: z.string().nullable(),
    content: z.string(),
    created_by: z.string().nullable(),
    created_at: wireTimestamp,
    updated_at: wireTimestamp.nullable(),
  })
  .passthrough();
export type CrmMasterEntryNote = z.infer<typeof crmMasterEntryNoteSchema>;

/** Allegato di una pipeline entry. */
export const crmMasterEntryAttachmentSchema = z
  .object({
    id: z.string(),
    entry_id: z.string().nullable(),
    filename: z.string(),
    original_name: z.string().nullable(),
    mime_type: z.string().nullable(),
    size_bytes: z.number().nullable(),
    uploaded_by: z.string().nullable(),
    created_at: wireTimestamp,
  })
  .passthrough();
export type CrmMasterEntryAttachment = z.infer<typeof crmMasterEntryAttachmentSchema>;

/** Attività di una pipeline entry. */
export const crmMasterEntryActivitySchema = z
  .object({
    id: z.string(),
    entry_id: z.string().nullable(),
    type: z.string(),
    title: z.string(),
    description: z.string().nullable(),
    due_date: wireTimestamp.nullable(),
    completed: z.boolean().nullable(),
    completed_at: wireTimestamp.nullable(),
    created_by: z.string().nullable(),
    created_at: wireTimestamp,
  })
  .passthrough();
export type CrmMasterEntryActivity = z.infer<typeof crmMasterEntryActivitySchema>;

/** Voce storico cambi stage di una pipeline entry. */
export const crmMasterEntryHistoryItemSchema = z
  .object({
    id: z.string(),
    entry_id: z.string().nullable(),
    from_stage_id: z.string().nullable(),
    to_stage_id: z.string().nullable(),
    changed_at: wireTimestamp,
    changed_by: z.string().nullable(),
    from_stage_name: z.string().nullable(),
    from_stage_color: z.string().nullable(),
    to_stage_name: z.string().nullable(),
    to_stage_color: z.string().nullable(),
  })
  .passthrough();
export type CrmMasterEntryHistoryItem = z.infer<typeof crmMasterEntryHistoryItemSchema>;

/** Preventivo linkato dentro il detail di una entry. */
export const crmMasterEntryLinkedQuoteSchema = z
  .object({
    id: z.string(),
    quote_number: z.string().nullable(),
    status: z.string().nullable(),
    total_gross: wireNumeric.nullable(),
    created_at: wireTimestamp.nullable(),
  })
  .passthrough();
export type CrmMasterEntryLinkedQuote = z.infer<typeof crmMasterEntryLinkedQuoteSchema>;

/** Contratto linkato dentro il detail di una entry. */
export const crmMasterEntryLinkedContractSchema = z
  .object({
    id: z.string(),
    contract_number: z.string().nullable(),
    status: z.string().nullable(),
    created_at: wireTimestamp.nullable(),
  })
  .passthrough();
export type CrmMasterEntryLinkedContract = z.infer<typeof crmMasterEntryLinkedContractSchema>;

/** GET /pipeline/entries/:id/detail → entry + liste correlate. */
export const crmMasterEntryDetailSchema = crmMasterPipelineEntrySchema
  .extend({
    notes_list: z.array(crmMasterEntryNoteSchema).optional(),
    attachments_list: z.array(crmMasterEntryAttachmentSchema).optional(),
    activities_list: z.array(crmMasterEntryActivitySchema).optional(),
    history: z.array(crmMasterEntryHistoryItemSchema).optional(),
    linked_quotes: z.array(crmMasterEntryLinkedQuoteSchema).optional(),
    linked_contracts: z.array(crmMasterEntryLinkedContractSchema).optional(),
  })
  .passthrough();
export type CrmMasterEntryDetail = z.infer<typeof crmMasterEntryDetailSchema>;

/** GET /pipeline/entries/:id/conversion-data → entry + tenant/stage slug. */
export const crmMasterLeadConversionDataSchema = crmMasterPipelineEntrySchema.passthrough();
export type CrmMasterLeadConversionData = z.infer<typeof crmMasterLeadConversionDataSchema>;

/** GET /pipeline/check-duplicate → possibili duplicati. */
export const crmMasterDuplicateWarningSchema = z
  .object({
    id: z.string(),
    company_name: z.string().nullable(),
    contact_name: z.string().nullable(),
    vat_number: z.string().nullable(),
    contact_email: z.string().nullable(),
    stage_name: z.string().nullable(),
    stage_slug: z.string().nullable(),
  })
  .passthrough();
export type CrmMasterDuplicateWarning = z.infer<typeof crmMasterDuplicateWarningSchema>;

/** Campo form lead dentro fields jsonb (contratto scritto dallo stesso FE). */
export const crmMasterLeadFormFieldSchema = z
  .object({
    label: z.string().optional(),
    type: z.string().optional(),
    required: z.boolean().optional(),
    placeholder: z.string().optional(),
    options: z.string().optional(), // CSV di opzioni per i select
  })
  .passthrough();
export type CrmMasterLeadFormField = z.infer<typeof crmMasterLeadFormFieldSchema>;

/** Stile form lead dentro style jsonb (contratto scritto dallo stesso FE). */
export const crmMasterLeadFormStyleSchema = z
  .object({
    primary_color: z.string().optional(),
    button_text: z.string().optional(),
  })
  .passthrough();
export type CrmMasterLeadFormStyle = z.infer<typeof crmMasterLeadFormStyleSchema>;

/** GET /lead-forms → form pubblici lead (fields/style jsonb tipizzati). */
export const crmMasterLeadFormSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    fields: z.array(crmMasterLeadFormFieldSchema).nullable(),
    target_stage: z.string().nullable(),
    default_lead_source: z.string().nullable(),
    style: crmMasterLeadFormStyleSchema.nullable(),
    is_active: z.boolean().nullable(),
    submissions_count: z.number().nullable(),
    created_at: wireTimestamp,
    updated_at: wireTimestamp.nullable(),
    target_stage_id: z.string().nullable(),
    tenant_id: z.string().nullable(),
    target_stage_name: z.string().nullable().optional(),
  })
  .passthrough();
export type CrmMasterLeadForm = z.infer<typeof crmMasterLeadFormSchema>;

// ============================================================================
// PREVENTIVI (master.quotes / master.quote_items)
// ============================================================================

/** Riga item preventivo (tabella reale, numeric → wireNumeric). */
export const crmMasterQuoteItemSchema = z
  .object({
    id: z.string(),
    quote_id: z.string().nullable(),
    product_id: z.string().nullable(),
    description: z.string().nullable(),
    quantity: wireNumeric.nullable(),
    unit_price: wireNumeric.nullable(),
    discount_percent: wireNumeric.nullable(),
    vat_rate: wireNumeric.nullable(),
    line_total_net: wireNumeric.nullable(),
    line_total_gross: wireNumeric.nullable(),
    sort_order: z.number().nullable(),
    vat_code: z.string().nullable(),
    product_name: z.string().nullable().optional(), // join products
    product_code: z.string().nullable().optional(), // join products
  })
  .passthrough();
export type CrmMasterQuoteItem = z.infer<typeof crmMasterQuoteItemSchema>;

/**
 * GET /quotes (lista, array nudo) e /quotes/:id (detail + items).
 * quotes.* + alias lead/tenant da LEFT JOIN.
 */
export const crmMasterQuoteSchema = z
  .object({
    id: z.string(),
    quote_number: z.string(),
    tenant_id: z.string().nullable(),
    status: z.string(),
    issue_date: wireTimestamp.nullable(),
    validity_date: wireTimestamp.nullable(),
    subtotal_net: wireNumeric.nullable(),
    total_vat: wireNumeric.nullable(),
    total_gross: wireNumeric.nullable(),
    notes: z.string().nullable(),
    payment_terms: z.string().nullable(),
    entry_fee: wireNumeric.nullable(),
    installments_count: z.number().nullable(),
    installment_frequency: z.string().nullable(),
    sent_at: wireTimestamp.nullable(),
    accepted_at: wireTimestamp.nullable(),
    rejected_at: wireTimestamp.nullable(),
    created_at: wireTimestamp,
    updated_at: wireTimestamp,
    pipeline_entry_id: z.string().nullable(),
    revision_at: wireTimestamp.nullable(),
    archived_at: wireTimestamp.nullable(),
    // Alias join (lista/detail).
    tenant_name: z.string().nullable().optional(),
    contact_email: z.string().nullable().optional(),
    lead_company_name: z.string().nullable().optional(),
    lead_contact_name: z.string().nullable().optional(),
    lead_contact_email: z.string().nullable().optional(),
    lead_stage_name: z.string().nullable().optional(),
    lead_stage_slug: z.string().nullable().optional(),
    // Solo detail.
    items: z.array(crmMasterQuoteItemSchema).optional(),
  })
  .passthrough();
export type CrmMasterQuote = z.infer<typeof crmMasterQuoteSchema>;

/** GET /quotes/dashboard → aggregati preventivi. */
export const crmMasterQuotesDashboardSchema = z
  .object({
    total_quotes: z.number(),
    total_value: wireNumeric,
    accepted_value: wireNumeric,
    pending_value: wireNumeric,
    this_month_count: z.number(),
    this_month_value: wireNumeric,
    conversion_rate: z.number(),
    by_status: z.record(z.number()),
  })
  .passthrough();
export type CrmMasterQuotesDashboard = z.infer<typeof crmMasterQuotesDashboardSchema>;

/**
 * GET /quotes/:id/payment-info. Se il preventivo non ha pipeline entry la
 * risposta contiene SOLO { has_payment_plan:false, payment_method_configured:false }
 * → tutti gli altri campi optional. installment_plan è jsonb (numeri = stringhe).
 */
export const crmMasterQuotePaymentInfoSchema = z
  .object({
    has_payment_plan: z.boolean(),
    payment_method_configured: z.boolean(),
    payment_plan_status: z.string().nullable().optional(),
    installment_plan: z.unknown().optional(),
    charge_first_immediately: z.boolean().optional(),
    duration_months: z.number().nullable().optional(),
    frequency: z.string().nullable().optional(),
    start_date: wireTimestamp.nullable().optional(),
    end_date: wireTimestamp.nullable().optional(),
    total_amount: wireNumeric.nullable().optional(),
  })
  .passthrough();
export type CrmMasterQuotePaymentInfo = z.infer<typeof crmMasterQuotePaymentInfoSchema>;

/**
 * GET /payment-plans (lista globale) e /payment-plans/:quoteId (per quote).
 * payment_plans.* + alias join (subset diverso per endpoint → optional).
 */
export const crmMasterPaymentPlanSchema = z
  .object({
    id: z.string(),
    quote_id: z.string().nullable(),
    tenant_id: z.string().nullable(),
    installment_plan: z.unknown(), // jsonb: array o {installments, charge_first_immediately}
    duration_months: z.number().nullable(),
    frequency: z.string().nullable(),
    start_date: wireTimestamp.nullable(),
    end_date: wireTimestamp.nullable(),
    total_amount: wireNumeric.nullable(),
    status: z.string(),
    version: z.number().nullable(),
    refused_at: wireTimestamp.nullable(),
    refused_reason: z.string().nullable(),
    accepted_at: wireTimestamp.nullable(),
    created_at: wireTimestamp,
    updated_at: wireTimestamp,
    pipeline_entry_id: z.string().nullable(),
    // Alias join.
    quote_number: z.string().nullable().optional(),
    quote_total: wireNumeric.nullable().optional(),
    tenant_name: z.string().nullable().optional(),
    tenant_email: z.string().nullable().optional(),
    stripe_pm_type: z.string().nullable().optional(),
    stripe_pm_last4: z.string().nullable().optional(),
    stripe_pm_brand: z.string().nullable().optional(),
    stripe_customer_id: z.string().nullable().optional(),
    contract_number: z.string().nullable().optional(),
    contract_status: z.string().nullable().optional(),
  })
  .passthrough();
export type CrmMasterPaymentPlan = z.infer<typeof crmMasterPaymentPlanSchema>;

// ============================================================================
// CONTRATTI (master.contracts)
// ============================================================================

/**
 * GET /contracts | /contracts/pipeline | /contracts/:id.
 * contracts.* (colonne fisiche) + alias join per proiezione → optional.
 */
export const crmMasterContractSchema = z
  .object({
    id: z.string(),
    contract_number: z.string(),
    tenant_id: z.string().nullable(),
    quote_id: z.string().nullable(),
    status: z.string().nullable(),
    template_content: z.string().nullable(),
    compiled_content: z.string().nullable(),
    sign_token: z.string().nullable(),
    sign_token_expires_at: wireTimestamp.nullable(),
    otp_code: z.string().nullable(),
    otp_expires_at: wireTimestamp.nullable(),
    otp_attempts: z.number().nullable(),
    signed_at: wireTimestamp.nullable(),
    sent_at: wireTimestamp.nullable(),
    cancelled_at: wireTimestamp.nullable(),
    created_at: wireTimestamp.nullable(),
    updated_at: wireTimestamp.nullable(),
    payment_suspended: z.boolean().nullable(),
    template_id: z.string().nullable(),
    installment_plan: z.unknown(), // jsonb
    start_date: wireTimestamp.nullable(),
    end_date: wireTimestamp.nullable(),
    duration_months: z.number().nullable(),
    contract_pipeline_status: z.string().nullable(),
    pipeline_entry_id: z.string().nullable(),
    payment_suspended_at: wireTimestamp.nullable(),
    payment_suspended_by: z.string().nullable(),
    payment_suspension_reason: z.string().nullable(),
    parent_contract_id: z.string().nullable(),
    addon_meta: z.unknown(), // jsonb
    voip_numbers: z.unknown(), // jsonb
    voip_tariffs_snapshot: z.unknown(), // jsonb
    regenerated_compiled_content: z.string().nullable().optional(),
    regenerated_compiled_content_hash: z.string().nullable().optional(),
    regenerated_compiled_content_at: wireTimestamp.nullable().optional(),
    regenerated_compiled_content_reason: z.string().nullable().optional(),
    // Alias join (lista/pipeline/detail).
    tenant_name: z.string().nullable().optional(),
    contact_email: z.string().nullable().optional(),
    contact_first_name: z.string().nullable().optional(),
    contact_last_name: z.string().nullable().optional(),
    client_vat: z.string().nullable().optional(),
    client_address: z.string().nullable().optional(),
    client_city: z.string().nullable().optional(),
    client_province: z.string().nullable().optional(),
    quote_number: z.string().nullable().optional(),
    quote_total: wireNumeric.nullable().optional(),
    quote_total_gross: wireNumeric.nullable().optional(),
    parent_contract_number: z.string().nullable().optional(),
    parent_signed_at: wireTimestamp.nullable().optional(),
    // Solo /contracts/:id/preview: ultima firma raccolta (o null).
    signature: z
      .object({
        signer_name: z.string().nullable().optional(),
        signer_email: z.string().nullable().optional(),
        signed_at: wireTimestamp.nullable().optional(),
        otp_used: z.string().nullable().optional(),
        signer_ip: z.string().nullable().optional(),
        user_agent: z.string().nullable().optional(),
        ip_address: z.string().nullable().optional(),
        document_hash: z.string().nullable().optional(),
      })
      .passthrough()
      .nullable()
      .optional(),
  })
  .passthrough();
export type CrmMasterContract = z.infer<typeof crmMasterContractSchema>;

/** GET /contracts/dashboard → aggregati contratti. */
export const crmMasterContractsDashboardSchema = z
  .object({
    total_contracts: z.number(),
    total_value: wireNumeric,
    active_value: wireNumeric,
    expiring_count: z.number(),
    signed_this_month: z.number(),
    avg_duration_months: z.number(),
    by_status: z.record(z.number()),
    by_pipeline: z.record(z.number()),
  })
  .passthrough();
export type CrmMasterContractsDashboard = z.infer<typeof crmMasterContractsDashboardSchema>;

/** GET /contracts/:id/signatures → SELECT * FROM master.contract_signatures. */
export const crmMasterContractSignatureSchema = z
  .object({
    id: z.string(),
    contract_id: z.string().nullable(),
    signer_email: z.string().nullable(),
    signer_name: z.string().nullable(),
    signer_ip: z.string().nullable(),
    user_agent: z.string().nullable(),
    document_hash: z.string().nullable(),
    otp_used: z.string().nullable(),
    signed_at: wireTimestamp.nullable(),
  })
  .passthrough();
export type CrmMasterContractSignature = z.infer<typeof crmMasterContractSignatureSchema>;

/** GET /contracts/:id/payment-audit-log → SELECT * master.payment_audit_log. */
export const crmMasterPaymentAuditLogEntrySchema = z
  .object({
    id: z.string(),
    contract_id: z.string().nullable(),
    pipeline_entry_id: z.string().nullable(),
    action: z.string(),
    actor: z.string().nullable(),
    reason: z.string().nullable(),
    // jsonb — il BE scrive { contract_number, days_suspended, installments_rescheduled }.
    details: z
      .object({ days_suspended: z.number().nullable().optional() })
      .passthrough()
      .nullable()
      .optional(),
    created_at: wireTimestamp.nullable(),
  })
  .passthrough();
export type CrmMasterPaymentAuditLogEntry = z.infer<typeof crmMasterPaymentAuditLogEntrySchema>;

/** GET /contract-templates → SELECT * FROM master.contract_templates. */
export const crmMasterContractTemplateSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    html_content: z.string(),
    is_default: z.boolean().nullable(),
    is_active: z.boolean().nullable(),
    created_at: wireTimestamp.nullable(),
    updated_at: wireTimestamp.nullable(),
    is_addon: z.boolean().nullable(),
    requires_voip_attachments: z.boolean().nullable(),
  })
  .passthrough();
export type CrmMasterContractTemplate = z.infer<typeof crmMasterContractTemplateSchema>;

// ============================================================================
// FATTURE (master.invoices / master.invoice_installments)
// ============================================================================

/**
 * Riga rata — invoice_installments.* + alias join (getInstallments /
 * failed / diagnose usano proiezioni diverse → alias optional).
 */
export const crmMasterInstallmentSchema = z
  .object({
    id: z.string(),
    invoice_id: z.string().nullable(),
    tenant_id: z.string().nullable(),
    installment_number: z.number().nullable(),
    amount: wireNumeric.nullable(),
    due_date: wireTimestamp.nullable(),
    status: z.string().nullable(),
    paid_at: wireTimestamp.nullable(),
    stripe_invoice_id: z.string().nullable(),
    stripe_payment_intent_id: z.string().nullable(),
    fic_payment_status: z.string().nullable(),
    is_entry_fee: z.boolean().nullable(),
    notes: z.string().nullable(),
    created_at: wireTimestamp.nullable(),
    updated_at: wireTimestamp.nullable(),
    reminder_sent: z.boolean().nullable(),
    retry_count: z.number().nullable(),
    max_retries: z.number().nullable(),
    fic_document_id: z.number().nullable(),
    fic_sdi_status: z.string().nullable(),
    pipeline_entry_id: z.string().nullable(),
    // Alias join.
    tenant_name: z.string().nullable().optional(),
    invoice_number: z.string().nullable().optional(),
    contract_id: z.string().nullable().optional(),
    invoice_total_gross: wireNumeric.nullable().optional(),
    contract_number: z.string().nullable().optional(),
    payment_suspended: z.boolean().nullable().optional(),
    tid: z.string().nullable().optional(), // solo /payments/failed
  })
  .passthrough();
export type CrmMasterInstallment = z.infer<typeof crmMasterInstallmentSchema>;

/**
 * GET /invoices (lista) e /invoices/:id (detail + installments).
 * invoices.* + alias join → optional.
 */
export const crmMasterInvoiceSchema = z
  .object({
    id: z.string(),
    invoice_number: z.string().nullable(),
    tenant_id: z.string().nullable(),
    contract_id: z.string().nullable(),
    quote_id: z.string().nullable(),
    status: z.string().nullable(),
    type: z.string().nullable(),
    issue_date: wireTimestamp.nullable(),
    due_date: wireTimestamp.nullable(),
    subtotal_net: wireNumeric.nullable(),
    total_vat: wireNumeric.nullable(),
    total_gross: wireNumeric.nullable(),
    amount_paid: wireNumeric.nullable(),
    fic_document_id: z.number().nullable(),
    fic_sdi_status: z.string().nullable(),
    e_invoice: z.boolean().nullable(),
    notes: z.string().nullable(),
    created_at: wireTimestamp.nullable(),
    updated_at: wireTimestamp.nullable(),
    credit_note_for_invoice_id: z.string().nullable(),
    credit_note_reason: z.string().nullable(),
    fic_credit_note_id: z.number().nullable(),
    pipeline_entry_id: z.string().nullable(),
    fic_number: z.string().nullable(),
    // Alias join (lista/detail).
    tenant_name: z.string().nullable().optional(),
    contact_email: z.string().nullable().optional(),
    pec: z.string().nullable().optional(),
    client_vat: z.string().nullable().optional(),
    client_address: z.string().nullable().optional(),
    client_city: z.string().nullable().optional(),
    contract_number: z.string().nullable().optional(),
    // Solo detail.
    installments: z.array(crmMasterInstallmentSchema).optional(),
  })
  .passthrough();
export type CrmMasterInvoice = z.infer<typeof crmMasterInvoiceSchema>;

/** GET /invoices/dashboard → aggregati fatture. */
export const crmMasterInvoicesDashboardSchema = z
  .object({
    total_invoices: z.number(),
    total_value: wireNumeric,
    paid_value: wireNumeric,
    overdue_value: wireNumeric,
    credit_notes_count: z.number(),
    credit_notes_value: wireNumeric,
    this_month_count: z.number(),
    this_month_value: wireNumeric,
    by_status: z.record(z.object({ count: z.number(), total: wireNumeric }).passthrough()),
    sdi_stats: z.record(z.number()),
  })
  .passthrough();
export type CrmMasterInvoicesDashboard = z.infer<typeof crmMasterInvoicesDashboardSchema>;

/**
 * GET /invoices/check-contract/:contractId. Se nessuna fattura esiste la
 * risposta è solo { existing:false } → campi aggregati optional.
 */
export const crmMasterInvoiceCheckSchema = z
  .object({
    existing: z.boolean(),
    invoice_number: z.string().nullable().optional(),
    installments_count: z.number().optional(),
    total_gross: wireNumeric.optional(),
    paid_count: z.number().optional(),
    paid_total: wireNumeric.optional(),
  })
  .passthrough();
export type CrmMasterInvoiceCheck = z.infer<typeof crmMasterInvoiceCheckSchema>;

/** GET /fic/sync-logs → SELECT * FROM master.fic_sync_log. */
export const crmMasterFicSyncLogSchema = z
  .object({
    id: z.string(),
    entity_type: z.string().nullable(),
    entity_id: z.string().nullable(),
    fic_id: z.number().nullable(),
    action: z.string().nullable(),
    status: z.string().nullable(),
    error_message: z.string().nullable(),
    synced_at: wireTimestamp.nullable(),
  })
  .passthrough();
export type CrmMasterFicSyncLog = z.infer<typeof crmMasterFicSyncLogSchema>;

/** Riga errore Stripe dentro /sync-errors. */
export const crmMasterStripeErrorRowSchema = z
  .object({
    id: z.string(),
    installment_id: z.string().nullable(),
    stripe_invoice_id: z.string().nullable(),
    amount: wireNumeric.nullable(),
    currency: z.string().nullable(),
    status: z.string().nullable(),
    failure_message: z.string().nullable(),
    created_at: wireTimestamp.nullable(),
    installment_number: z.number().nullable(),
    due_date: wireTimestamp.nullable(),
    tenant_name: z.string().nullable(),
  })
  .passthrough();
export type CrmMasterStripeErrorRow = z.infer<typeof crmMasterStripeErrorRowSchema>;

/** Riga errore SdI dentro /sync-errors. */
export const crmMasterSdiErrorRowSchema = z
  .object({
    id: z.string(),
    invoice_number: z.string().nullable(),
    pipeline_entry_id: z.string().nullable(),
    fic_sdi_status: z.string().nullable(),
    type: z.string().nullable(),
    total_gross: wireNumeric.nullable(),
    issue_date: wireTimestamp.nullable(),
    updated_at: wireTimestamp.nullable(),
    tenant_name: z.string().nullable(),
  })
  .passthrough();
export type CrmMasterSdiErrorRow = z.infer<typeof crmMasterSdiErrorRowSchema>;

/**
 * GET /sync-errors → report errori sincronizzazione. Con ?type= la risposta
 * contiene solo la sezione richiesta → tutte optional.
 */
export const crmMasterSyncErrorsReportSchema = z
  .object({
    fic_errors: z.array(crmMasterFicSyncLogSchema).optional(),
    stripe_errors: z.array(crmMasterStripeErrorRowSchema).optional(),
    sdi_errors: z.array(crmMasterSdiErrorRowSchema).optional(),
    total_errors: z.number().optional(),
  })
  .passthrough();
export type CrmMasterSyncErrorsReport = z.infer<typeof crmMasterSyncErrorsReportSchema>;

// ============================================================================
// PAGAMENTI
// ============================================================================

/** GET /payments/dashboard → aggregati incassi (tutti SUM numeric). */
export const crmMasterPaymentsDashboardSchema = z
  .object({
    total_collected: wireNumeric,
    total_pending: wireNumeric,
    total_overdue: wireNumeric,
    total_failed: wireNumeric,
    this_month_collected: wireNumeric,
    forecast_30d: wireNumeric,
    total_paid: wireNumeric,
    total_due: wireNumeric,
    next_30_days_forecast: wireNumeric,
  })
  .passthrough();
export type CrmMasterPaymentsDashboard = z.infer<typeof crmMasterPaymentsDashboardSchema>;

/** GET /payments/mrr → punti MRR mensili (mrr = SUM numeric || 0). */
export const crmMasterMrrPointSchema = z
  .object({
    month: wireTimestamp.nullable(),
    label: z.string(),
    mrr: wireNumeric,
  })
  .passthrough();
export type CrmMasterMrrPoint = z.infer<typeof crmMasterMrrPointSchema>;

/** GET /payments/aging → fasce scaduto (SUM numeric). */
export const crmMasterAgingReportSchema = z
  .object({
    not_due: wireNumeric,
    '0_30': wireNumeric,
    '31_60': wireNumeric,
    '61_90': wireNumeric,
    over_90: wireNumeric,
  })
  .passthrough();
export type CrmMasterAgingReport = z.infer<typeof crmMasterAgingReportSchema>;

/** Riga pagamento Stripe — SELECT sp.* FROM master.stripe_payments. */
export const crmMasterStripePaymentSchema = z
  .object({
    id: z.string(),
    installment_id: z.string().nullable(),
    tenant_id: z.string().nullable(),
    stripe_payment_intent_id: z.string().nullable(),
    stripe_invoice_id: z.string().nullable(),
    stripe_charge_id: z.string().nullable(),
    amount: wireNumeric.nullable(),
    currency: z.string().nullable(),
    status: z.string().nullable(),
    payment_method_type: z.string().nullable(),
    failure_code: z.string().nullable(),
    failure_message: z.string().nullable(),
    attempt_count: z.number().nullable(),
    metadata: z.unknown(), // jsonb
    created_at: wireTimestamp.nullable(),
    pipeline_entry_id: z.string().nullable(),
    paid_at: wireTimestamp.nullable(),
  })
  .passthrough();
export type CrmMasterStripePayment = z.infer<typeof crmMasterStripePaymentSchema>;

/** GET /payments/client/:tenantId → storico pagamenti di un cliente. */
export const crmMasterClientPaymentsSchema = z
  .object({
    invoices: z.array(crmMasterInvoiceSchema),
    installments: z.array(crmMasterInstallmentSchema),
    payments: z.array(crmMasterStripePaymentSchema),
    lifetime_value: z.number(), // reduce() JS
    outstanding: z.number(), // reduce() JS
  })
  .passthrough();
export type CrmMasterClientPayments = z.infer<typeof crmMasterClientPaymentsSchema>;

/** Endpoint webhook Stripe dentro /payments/webhook-test. */
export const crmMasterWebhookEndpointSchema = z
  .object({
    id: z.string(),
    url: z.string(),
    status: z.string(),
    enabled_events: z.array(z.string()).optional(),
    enabled_events_count: z.number().optional(),
    api_version: z.string().nullable().optional(),
  })
  .passthrough();
export type CrmMasterWebhookEndpoint = z.infer<typeof crmMasterWebhookEndpointSchema>;

/** Evento Stripe recente dentro /payments/webhook-test. */
export const crmMasterWebhookEventSchema = z
  .object({
    id: z.string(),
    type: z.string(),
    created: z.union([z.string(), z.number()]).optional(),
    pending_webhooks: z.number().optional(),
  })
  .passthrough();
export type CrmMasterWebhookEvent = z.infer<typeof crmMasterWebhookEventSchema>;

/** GET /payments/webhook-test → check configurazione webhook Stripe. */
export const crmMasterWebhookTestSchema = z
  .object({
    stripe_configured: z.boolean(),
    stripe_connected: z.boolean().optional(),
    webhook_secret_configured: z.boolean().optional(),
    is_live_mode: z.boolean().nullable().optional(),
    webhook_endpoints: z.array(crmMasterWebhookEndpointSchema).optional(),
    recent_events: z.array(crmMasterWebhookEventSchema).optional(),
    issues: z.array(z.string()).optional(),
    stripe_api_error: z.string().optional(),
  })
  .passthrough();
export type CrmMasterWebhookTest = z.infer<typeof crmMasterWebhookTestSchema>;

/** Carta trovata su Stripe dentro /payments/diagnose. */
export const crmMasterDiagnosisCardSchema = z
  .object({
    id: z.string(),
    brand: z.string().nullable().optional(),
    last4: z.string().nullable().optional(),
    exp: z.string().optional(),
  })
  .passthrough();
export type CrmMasterDiagnosisCard = z.infer<typeof crmMasterDiagnosisCardSchema>;

/** SEPA trovato su Stripe dentro /payments/diagnose. */
export const crmMasterDiagnosisSepaSchema = z
  .object({
    id: z.string(),
    last4: z.string().nullable().optional(),
    bank: z.string().nullable().optional(),
  })
  .passthrough();
export type CrmMasterDiagnosisSepa = z.infer<typeof crmMasterDiagnosisSepaSchema>;

/** Rata dentro /payments/diagnose (proiezione esplicita). */
export const crmMasterDiagnosisInstallmentSchema = z
  .object({
    id: z.string(),
    amount: wireNumeric.nullable(),
    due_date: wireTimestamp.nullable(),
    status: z.string().nullable(),
    installment_number: z.number().nullable(),
    stripe_invoice_id: z.string().nullable(),
    retry_count: z.number().nullable(),
    notes: z.string().nullable(),
    is_entry_fee: z.boolean().nullable(),
    invoice_number: z.string().nullable(),
    contract_id: z.string().nullable(),
  })
  .passthrough();
export type CrmMasterDiagnosisInstallment = z.infer<typeof crmMasterDiagnosisInstallmentSchema>;

/** GET /payments/diagnose/:pipelineEntryId → diagnosi setup pagamenti. */
export const crmMasterStripeDiagnosisSchema = z
  .object({
    pipeline_entry_id: z.string().optional(),
    issues: z.array(z.string()),
    ok: z.array(z.string()),
    client_name: z.string().nullable().optional(),
    client_email: z.string().nullable().optional(),
    stripe_live_mode: z.boolean().nullable().optional(),
    payment_methods_on_stripe: z
      .object({
        cards: z.array(crmMasterDiagnosisCardSchema),
        sepa: z.array(crmMasterDiagnosisSepaSchema),
      })
      .passthrough()
      .optional(),
    contracts: z
      .array(
        z
          .object({
            id: z.string(),
            contract_number: z.string().nullable(),
            status: z.string().nullable(),
            contract_pipeline_status: z.string().nullable(),
            payment_suspended: z.boolean().nullable(),
            start_date: wireTimestamp.nullable(),
            end_date: wireTimestamp.nullable(),
          })
          .passthrough()
      )
      .optional(),
    installments: z
      .object({
        total: z.number(),
        pending: z.array(crmMasterDiagnosisInstallmentSchema),
        overdue: z.array(crmMasterDiagnosisInstallmentSchema),
        paid: z.array(crmMasterDiagnosisInstallmentSchema),
        failed: z.array(crmMasterDiagnosisInstallmentSchema),
        processing: z.array(crmMasterDiagnosisInstallmentSchema),
      })
      .passthrough()
      .optional(),
    due_installments: z.array(crmMasterDiagnosisInstallmentSchema).optional(),
    recent_cron_runs: z
      .array(
        z
          .object({
            job_type: z.string(),
            status: z.string().nullable(),
            started_at: wireTimestamp.nullable(),
            processed_count: z.number().nullable(),
            success_count: z.number().nullable(),
            error_count: z.number().nullable(),
            error_message: z.string().nullable(),
            result_details: z.unknown(),
          })
          .passthrough()
      )
      .optional(),
    verdict: z.string().optional(),
  })
  .passthrough();
export type CrmMasterStripeDiagnosis = z.infer<typeof crmMasterStripeDiagnosisSchema>;

/** GET /cron-jobs/history → SELECT * FROM master.cron_job_logs (id integer). */
export const crmMasterCronRunSchema = z
  .object({
    id: z.number(),
    job_type: z.string(),
    started_at: wireTimestamp.nullable(),
    finished_at: wireTimestamp.nullable(),
    duration_ms: z.number().nullable(),
    status: z.string().nullable(),
    processed_count: z.number().nullable(),
    success_count: z.number().nullable(),
    error_count: z.number().nullable(),
    error_message: z.string().nullable(),
    result_details: z.unknown(), // jsonb
    completed_at: wireTimestamp.nullable(),
  })
  .passthrough();
export type CrmMasterCronRun = z.infer<typeof crmMasterCronRunSchema>;

/** GET /cron-jobs/upcoming → prossime rate da addebitare. */
export const crmMasterUpcomingChargeSchema = z
  .object({
    id: z.string(),
    installment_number: z.number().nullable(),
    amount: wireNumeric.nullable(),
    due_date: wireTimestamp.nullable(),
    status: z.string().nullable(),
    is_entry_fee: z.boolean().nullable(),
    invoice_number: z.string().nullable(),
    contract_id: z.string().nullable(),
    tenant_name: z.string().nullable(),
    stripe_customer_id: z.string().nullable(),
    payment_suspended: z.boolean().nullable(),
  })
  .passthrough();
export type CrmMasterUpcomingCharge = z.infer<typeof crmMasterUpcomingChargeSchema>;

// ============================================================================
// IMPOSTAZIONI
// ============================================================================

/** GET /voip-tariffs → proiezione esplicita da master.voip_tariffs. */
export const crmMasterVoipTariffSchema = z
  .object({
    id: z.string(),
    tariff_key: z.string(),
    placeholder_key: z.string(),
    label: z.string(),
    unit_label: z.string(),
    price_per_min: wireNumeric,
    sort_order: z.number().nullable(),
    is_active: z.boolean(),
    updated_at: wireTimestamp.nullable(),
  })
  .passthrough();
export type CrmMasterVoipTariff = z.infer<typeof crmMasterVoipTariffSchema>;

/**
 * GET /stripe/config → riga config senza chiavi cifrate + hint.
 * La risposta può essere null se la config non esiste ancora.
 */
export const crmMasterStripeConfigSchema = z
  .object({
    id: z.string().optional(),
    publishable_key: z.string().nullable().optional(),
    is_live_mode: z.boolean().nullable().optional(),
    is_connected: z.boolean().nullable().optional(),
    is_active: z.boolean().nullable().optional(),
    created_at: wireTimestamp.nullable().optional(),
    updated_at: wireTimestamp.nullable().optional(),
    secret_key_hint: z.string().nullable().optional(),
    webhook_secret_hint: z.string().nullable().optional(),
  })
  .passthrough();
export type CrmMasterStripeConfig = z.infer<typeof crmMasterStripeConfigSchema>;

/**
 * GET /fic/config → riga config Fatture in Cloud senza token cifrati + hint.
 * La risposta può essere null se la config non esiste ancora.
 */
export const crmMasterFicConfigSchema = z
  .object({
    id: z.string().optional(),
    client_id: z.string().nullable().optional(),
    company_id: z.number().nullable().optional(),
    is_connected: z.boolean().nullable().optional(),
    token_expires_at: wireTimestamp.nullable().optional(),
    webhook_subscription_id: z.string().nullable().optional(),
    created_at: wireTimestamp.nullable().optional(),
    updated_at: wireTimestamp.nullable().optional(),
    client_secret_hint: z.string().nullable().optional(),
  })
  .passthrough();
export type CrmMasterFicConfig = z.infer<typeof crmMasterFicConfigSchema>;
